import { Box, Icon } from '@everlywell/ui-kit';
import '@phosphor-icons/web/fill';
import '@phosphor-icons/web/regular';
import '@phosphor-icons/web/light';
import '@phosphor-icons/web/duotone';
import '@phosphor-icons/web/thin';
import '@phosphor-icons/web/bold';
import { icons } from '@phosphor-icons/core';
import React, { ComponentPropsWithRef, forwardRef, Ref } from 'react';
import { ExtractType } from 'utils/types/utilityTypes';

/**
 * `icons` is an array of objects that look like this:
 * {
 *  name: 'airplane',
 *  pascal_name: 'Airplane',
 * }
 *
 * sometimes they will have an alias property that looks like this:
 * {
 *  name: 'airplane',
 *  pascal_name: 'Airplane',
 *  alias: {
 *    pascal_name: 'Plane',
 *    name: 'plane',
 *  },
 * }
 */
type IconName = (typeof icons)[number]['pascal_name'];
// extract the object types that have the alias property, then get the alias's pascal_name
type IconAlias = ExtractType<
  (typeof icons)[number],
  {
    alias: {
      pascal_name: string;
      name: string;
    };
  }
>['alias']['pascal_name'];

// Create a map of icon pascal names to their snake-cased phosphor name
const allAvailableIcons = Object.entries(icons).reduce((acc, [key, value]) => {
  acc[value.pascal_name] = value.name;
  if ('alias' in value) {
    acc[value.alias.pascal_name] = value.name;
  }
  return acc;
}, {} as Record<IconName | IconAlias, string>);

export type IconKey = IconName | IconAlias;

export type DynamicIconProps = ComponentPropsWithRef<typeof Icon> & {
  icon: IconKey;
  weight?: 'fill' | 'regular' | 'light' | 'duotone' | 'thin' | 'bold';
};

/**
 * Wrapper around the Icon component that allows for dynamic icon selection.
 * This uses Phosphor Icon fonts, which are not as scalable as SVG icons.
 *
 * Prefer using SVG icons when possible without importing the entire phosphor icon library.
 */
const DynamicIcon = forwardRef((props: DynamicIconProps, ref) => {
  const { icon, weight, ...rest } = props;

  const iconName = allAvailableIcons[icon];

  let iconWeight = 'ph';
  switch (weight) {
    case 'fill':
      iconWeight = 'ph-fill';
      break;
    case 'light':
      iconWeight = 'ph-light';
      break;
    case 'duotone':
      iconWeight = 'ph-duotone';
      break;
    case 'thin':
      iconWeight = 'ph-thin';
      break;
    case 'bold':
      iconWeight = 'ph-bold';
      break;
  }
  return (
    <Icon
      {...rest}
      ref={ref as Ref<SVGElement>}
      as={Box}
      className={`${iconWeight} ph-${iconName}`}
    />
  );
});

export default DynamicIcon;
